import type React from "react"
import { useEffect, useState, useRef, useCallback } from "react"

interface ErrorRendererProps {
  error: string | Error | null | undefined
  title?: string
  className?: string
  onDismiss?: () => void
  dismissible?: boolean
  autoDismiss?: boolean
  autoDismissDelay?: number
}

export const ErrorRenderer: React.FC<ErrorRendererProps> = ({ error, title, className = "", onDismiss, dismissible = false, autoDismiss = true, autoDismissDelay = 5000 }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [lastError, setLastError] = useState("")
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleDismiss = useCallback(
    (isManual: boolean = true) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setIsVisible(false)
      if (isManual) {
        onDismiss?.()
      }
    },
    [onDismiss]
  )

  // If error is null/undefined, hide the component
  useEffect(() => {
    if (!error) {
      setIsVisible(false)
      setLastError("")
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      return
    }

    const errorTitle = title || (error instanceof Error ? "Error" : "Error")
    const errorMessage = error instanceof Error ? error.message : error
    const currentError = `${errorTitle}|${errorMessage}`

    if (currentError !== lastError) {
      setIsVisible(true)
      setLastError(currentError)

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // Set auto-dismiss timer if enabled
      if (dismissible && autoDismiss) {
        timeoutRef.current = setTimeout(() => {
          handleDismiss(false)
        }, autoDismissDelay)
      }
    }
  }, [error, title, lastError, autoDismiss, autoDismissDelay, dismissible, handleDismiss])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!isVisible || !error) {
    return null
  }

  const errorTitle = title || (error instanceof Error ? "Error" : "Error")
  const errorMessage = error instanceof Error ? error.message : error

  return (
    <div className={`mt-3 rounded-lg bg-red-50 p-4 ${className}`} role="alert" aria-live="polite">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h4 className="font-manrope font-semibold text-red-600">{errorTitle}</h4>
          <p className="text-base text-red-600">{errorMessage}</p>
        </div>
        {dismissible && (
          <button
            type="button"
            onClick={() => handleDismiss(true)}
            className="ml-4 shrink-0 rounded-full p-1 text-red-400 hover:bg-red-100 hover:text-red-600 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:outline-none"
            aria-label="Dismiss"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}
